import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { useInboundPackageByIdQuery, useSetDimensionsMutation, useGetItemDimensionsQuery } from 'api/warehouse';
import { WAREHOUSE_ROUTES } from 'constants/routes';

interface Dimensions {
  height: string;
  width: string;
  length: string;
  weight: string;
  lengthUnit: 'cm' | 'ft/in';
  weightUnit: 'g' | 'kg';
}

export const useItemDetails = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [quantityConfirmed, setQuantityConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dimensions, setDimensions] = useState<Dimensions>({
    height: '',
    width: '',
    length: '',
    weight: '',
    lengthUnit: 'cm',
    weightUnit: 'g',
  });

  const itemInfo = location.state as { itemId: string; sku: string } | undefined;

  const {
    data: order,
    isLoading,
    error,
  } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  const [submitDimensions] = useSetDimensionsMutation();

  // Fetch existing dimensions
  const { data: dimensionsData } = useGetItemDimensionsQuery(itemInfo?.itemId || '', {
    skip: !itemInfo?.itemId,
  });

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  useEffect(() => {
    if (!itemInfo) {
      message.warning('Please identify an item first');
      navigate(`/warehouse/inbound/${id}/capture`);
    }
  }, [itemInfo, id, navigate, message]);

  // Pre-fill dimensions if they exist
  useEffect(() => {
    if (dimensionsData?.data) {
      const dims = dimensionsData.data;
      setDimensions({
        height: dims.height?.toString() || '',
        width: dims.width?.toString() || '',
        length: dims.length?.toString() || '',
        weight: dims.weight?.toString() || '',
        lengthUnit: dims.lengthMetric === 'FT_IN' ? 'ft/in' : 'cm',
        weightUnit: dims.massUnit === 'KG' ? 'kg' : 'g',
      });
    }
  }, [dimensionsData]);

  // Get item data from order
  const itemData = order?.itemsResponseList?.find((item: any) => item.id === itemInfo?.itemId);

  const handleDimensionChange = useCallback((field: keyof Dimensions, value: string) => {
    setDimensions((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleUnitChange = useCallback((field: keyof Dimensions, value: string) => {
    setDimensions((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleQuantityConfirm = useCallback((e: any) => {
    setQuantityConfirmed(e.target.checked);
  }, []);

  const handleRaiseClaim = useCallback(() => {
    navigate(`/warehouse/inbound/${id}/raise-claim`, {
      state: { itemId: itemInfo?.itemId, sku: itemInfo?.sku },
    });
  }, [id, itemInfo, navigate]);

  const handleNext = useCallback(async () => {
    if (!itemInfo || !quantityConfirmed) return;

    try {
      setIsSubmitting(true);
      message.loading({ content: 'Saving item details...', key: 'save' });

      // Convert units to backend format
      const massUnit = dimensions.weightUnit === 'kg' ? 'KG' : 'G';
      const lengthMetric = dimensions.lengthUnit === 'ft/in' ? 'FT' : 'CM';

      const response: any = await submitDimensions({
        orderId: id || '',
        sku: itemInfo.sku,
        height: dimensions.height ? parseFloat(dimensions.height) : -1,
        width: dimensions.width ? parseFloat(dimensions.width) : -1,
        length: dimensions.length ? parseFloat(dimensions.length) : -1,
        weight: dimensions.weight ? parseFloat(dimensions.weight) : -1,
        massUnit,
        lengthMetric,
        quantityConfirmed: true,
      });

      if (response?.data?.success) {
        message.success({ content: 'Item details saved successfully', key: 'save' });
        // Pass packages data from API response to packaging page
        const packages = response.data.data || [];
        navigate(`/warehouse/inbound/${id}/packaging`, {
          state: { itemId: itemInfo.itemId, sku: itemInfo.sku, packages },
        });
      } else {
        throw new Error('Failed to save item details');
      }
    } catch (err: any) {
      message.error({
        content: err?.message || 'Failed to save item details',
        key: 'save',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [id, itemInfo, quantityConfirmed, dimensions, submitDimensions, navigate, message]);

  const completedCount =
    order?.itemsResponseList?.filter((item: any) => item.status === 'CHECKED' || item.status === 'CLAIMED').length || 0;

  const handleBack = useCallback(() => {
    // Go back to capture item page (previous step)
    navigate(WAREHOUSE_ROUTES.captureItem.replace(':id', id || ''), {
      state: itemInfo,
    });
  }, [id, navigate, itemInfo]);

  const totalCount = order?.itemsResponseList?.length || 0;

  return {
    isLoading,
    itemInfo,
    itemData,
    dimensions,
    quantityConfirmed,
    isSubmitting,
    handleDimensionChange,
    handleUnitChange,
    handleQuantityConfirm,
    handleNext,
    handleBack,
    handleRaiseClaim,
    completedCount,
    totalCount,
  };
};
